/**
 * seedUserData.ts  —  Auto-seed a realistic customer + 3 sites + 9 influent samples
 * for a newly created user.  Mirrors deploy/api/admin/seed-user.php exactly.
 *
 * Export:
 *   seedUserDataRemote(targetUserId, country, userName)  — calls PHP endpoint
 *   seedUserDataLocally(userId, country, userName)        — localStorage fallback
 */

import { getApiBase } from '@/lib/phpApi';
import { saveLocalInfluentSample, saveLocalSite } from '@/lib/hybridStorage';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SiteData {
  name: string;
  address: string;
  postcode: string;
  country: string;
  permitRef: string;
  permitIssued: string;
  permitExpiry: string;
  dischargeRoute: string;
  standard: string;
  samples: SampleData[];
}

interface SampleData {
  name: string;
  sampleType: string;
  notes: string;
  params: Record<string, number>;
}

interface CountryData {
  customerName: string;
  company: string;
  phone: string;
  email: string;
  address: string;
  country: string;
  standard: string;
  sites: SiteData[];
}

// ─── Country Data Builders ─────────────────────────────────────────────────────

function buildGbData(userName: string): CountryData {
  const year = new Date().getFullYear();
  return {
    customerName: `${userName} Operations`,
    company: `${userName} Water Services Ltd`,
    phone: '+44 1234 567890',
    email: `contact@${userName.toLowerCase().replace(/\s+/g, '')}-water.co.uk`,
    address: '12 Industrial Estate, Manchester, M1 2AB',
    country: 'GB',
    standard: 'UK_EA',
    sites: [
      {
        name: 'Manchester Municipal WWTP',
        address: '45 Sewage Works Road, Salford, M5 3QR',
        postcode: 'M5 3QR',
        country: 'GB',
        permitRef: `EPR/${year}/001`,
        permitIssued: `${year}-01-01`,
        permitExpiry: `${year + 5}-12-31`,
        dischargeRoute: 'River Irwell (Surface Water)',
        standard: 'UK_EA',
        samples: [
          {
            name: 'Municipal — Baseline (Normal Dry Weather)',
            sampleType: 'influent',
            notes: 'Dry weather flow sample. Typical domestic plus light industrial trade effluent consent.',
            params: {
              ph: 7.2, cod: 380, bod5: 195, tss: 245, oilgrease: 18, tn: 42, tp: 6.8,
              nh3n: 28, tds: 560, conductivity: 820, temperature: 14.5, turbidity: 85,
              alkalinity: 210, hardness: 280, chloride: 95, zinc: 0.18, chromium: 0.04,
              nickel: 0.06, copper: 0.08, lead: 0.03, arsenic: 0.005, flow: 18500,
            },
          },
          {
            name: 'Municipal — Peak Wet Weather Event',
            sampleType: 'influent',
            notes: 'Storm event — combined sewer overflow dilution. High flow, reduced concentrations.',
            params: {
              ph: 7.4, cod: 195, bod5: 98, tss: 380, oilgrease: 12, tn: 22, tp: 3.1,
              nh3n: 14, tds: 310, conductivity: 420, temperature: 12.0, turbidity: 220,
              alkalinity: 145, hardness: 195, chloride: 58, zinc: 0.32, chromium: 0.02,
              nickel: 0.04, copper: 0.05, lead: 0.07, arsenic: 0.003, flow: 42000,
            },
          },
          {
            name: 'Municipal — Industrial Discharge Incident',
            sampleType: 'influent',
            notes: 'Unplanned trade effluent discharge. Elevated COD and solids from nearby food factory.',
            params: {
              ph: 5.8, cod: 1850, bod5: 920, tss: 680, oilgrease: 95, tn: 88, tp: 22,
              nh3n: 62, tds: 1240, conductivity: 1850, temperature: 28, turbidity: 380,
              alkalinity: 85, hardness: 320, chloride: 185, zinc: 0.65, chromium: 0.14,
              nickel: 0.22, copper: 0.31, lead: 0.09, arsenic: 0.012, flow: 21000,
            },
          },
        ],
      },
      {
        name: 'Cheshire Dairy Processing Plant',
        address: '8 Dairy Lane, Crewe, CW1 5TH',
        postcode: 'CW1 5TH',
        country: 'GB',
        permitRef: `EPR/${year}/002`,
        permitIssued: `${year}-03-01`,
        permitExpiry: `${year + 4}-02-28`,
        dischargeRoute: 'Foul Sewer (Consent to Discharge)',
        standard: 'UK_EA',
        samples: [
          {
            name: 'Dairy — Normal Production (Morning Shift)',
            sampleType: 'influent',
            notes: 'Milk processing wastewater. Butter and cheese wash-down. BOD/COD ratio typical of dairy.',
            params: {
              ph: 7.0, cod: 2800, bod5: 1680, tss: 820, oilgrease: 180, tn: 65, tp: 28,
              nh3n: 42, tds: 1450, conductivity: 2100, temperature: 22, turbidity: 310,
              alkalinity: 380, hardness: 195, chloride: 220, zinc: 0.12, chromium: 0.01,
              nickel: 0.03, copper: 0.04, lead: 0.008, arsenic: 0.002, flow: 850,
            },
          },
          {
            name: 'Dairy — Peak Production (Max Output)',
            sampleType: 'influent',
            notes: 'Maximum production day — yoghurt and UHT milk processing combined.',
            params: {
              ph: 6.8, cod: 5200, bod5: 3120, tss: 1450, oilgrease: 420, tn: 115, tp: 48,
              nh3n: 78, tds: 2600, conductivity: 3800, temperature: 26, turbidity: 560,
              alkalinity: 280, hardness: 210, chloride: 380, zinc: 0.22, chromium: 0.02,
              nickel: 0.04, copper: 0.06, lead: 0.01, arsenic: 0.003, flow: 1250,
            },
          },
          {
            name: 'Dairy — CIP Discharge (Chemical Clean)',
            sampleType: 'influent',
            notes: 'Clean-in-place discharge. NaOH and HNO3 rinse water. pH extremes and elevated conductivity.',
            params: {
              ph: 11.8, cod: 3800, bod5: 950, tss: 280, oilgrease: 65, tn: 38, tp: 12,
              nh3n: 18, tds: 8500, conductivity: 12800, temperature: 55, turbidity: 145,
              alkalinity: 4200, hardness: 85, chloride: 2800, zinc: 0.08, chromium: 0.18,
              nickel: 0.12, copper: 0.14, lead: 0.04, arsenic: 0.006, flow: 320,
            },
          },
        ],
      },
      {
        name: 'Sheffield Industrial Chemical Works',
        address: '22 Steel Works Way, Sheffield, S9 2BT',
        postcode: 'S9 2BT',
        country: 'GB',
        permitRef: `EPR/${year}/003`,
        permitIssued: `${year}-06-01`,
        permitExpiry: `${year + 3}-05-31`,
        dischargeRoute: 'River Don (Surface Water)',
        standard: 'UK_EA',
        samples: [
          {
            name: 'Industrial — Standard Operations',
            sampleType: 'influent',
            notes: 'Metal finishing and surface treatment. Trade effluent consent EPR/2024/003.',
            params: {
              ph: 4.2, cod: 1650, bod5: 310, tss: 580, oilgrease: 62, tn: 28, tp: 4.5,
              nh3n: 15, tds: 3800, conductivity: 5400, temperature: 35, turbidity: 420,
              alkalinity: 45, hardness: 520, chloride: 1200, zinc: 8.5, chromium: 3.2,
              nickel: 2.8, copper: 1.6, lead: 0.85, arsenic: 0.18, flow: 380,
            },
          },
          {
            name: 'Industrial — High Load Shift',
            sampleType: 'influent',
            notes: 'Full electroplating bath turnover. Increased heavy metal concentrations.',
            params: {
              ph: 3.5, cod: 3200, bod5: 580, tss: 1200, oilgrease: 145, tn: 52, tp: 8.2,
              nh3n: 28, tds: 6800, conductivity: 9800, temperature: 42, turbidity: 820,
              alkalinity: 22, hardness: 780, chloride: 2400, zinc: 18.5, chromium: 8.4,
              nickel: 6.2, copper: 4.8, lead: 2.2, arsenic: 0.48, flow: 520,
            },
          },
          {
            name: 'Industrial — Spill Event (Acid Tank)',
            sampleType: 'influent',
            notes: 'Containment discharge following acid tank bund overflow. Emergency treatment required.',
            params: {
              ph: 1.8, cod: 8500, bod5: 420, tss: 2800, oilgrease: 280, tn: 85, tp: 18,
              nh3n: 42, tds: 18500, conductivity: 28000, temperature: 18, turbidity: 2400,
              alkalinity: 0, hardness: 1200, chloride: 8500, zinc: 45, chromium: 22,
              nickel: 18, copper: 12, lead: 6.5, arsenic: 1.8, flow: 95,
            },
          },
        ],
      },
    ],
  };
}

function buildAuData(userName: string): CountryData {
  const year = new Date().getFullYear();
  return {
    customerName: `${userName} Environmental`,
    company: `${userName} Water Management Pty Ltd`,
    phone: '+61 2 9876 5432',
    email: `info@${userName.toLowerCase().replace(/\s+/g, '')}-water.com.au`,
    address: '78 Industrial Boulevard, Parramatta, NSW 2150',
    country: 'AU',
    standard: 'AU_EPA',
    sites: [
      {
        name: 'Parramatta Regional WWTP',
        address: '12 Treatment Plant Road, Rydalmere, NSW 2116',
        postcode: '2116',
        country: 'AU',
        permitRef: `EPA-NSW-${year}-001`,
        permitIssued: `${year}-01-01`,
        permitExpiry: `${year + 4}-12-31`,
        dischargeRoute: 'Parramatta River (EPA Licensed)',
        standard: 'AU_EPA',
        samples: [
          {
            name: 'WWTP — Normal Dry Weather Flow',
            sampleType: 'influent',
            notes: 'Standard domestic wastewater. ANZECC/ARMCANZ 2000 monitoring — Schedule B1.',
            params: {
              ph: 7.3, cod: 360, bod5: 180, tss: 220, oilgrease: 22, tn: 38, tp: 7.2,
              nh3n: 26, tds: 580, conductivity: 890, temperature: 22, turbidity: 72,
              alkalinity: 195, hardness: 240, chloride: 105, zinc: 0.22, chromium: 0.03,
              nickel: 0.05, copper: 0.09, lead: 0.04, arsenic: 0.006,
              cadmium: 0.002, mercury: 0.0005, flow: 22000,
            },
          },
          {
            name: 'WWTP — Summer Storm Event',
            sampleType: 'influent',
            notes: 'Summer thunderstorm runoff. Elevated TSS from catchment drainage.',
            params: {
              ph: 7.6, cod: 185, bod5: 88, tss: 450, oilgrease: 14, tn: 19, tp: 2.8,
              nh3n: 12, tds: 320, conductivity: 460, temperature: 25, turbidity: 285,
              alkalinity: 125, hardness: 165, chloride: 68, zinc: 0.38, chromium: 0.02,
              nickel: 0.04, copper: 0.06, lead: 0.08, arsenic: 0.004,
              cadmium: 0.001, mercury: 0.0003, flow: 48000,
            },
          },
          {
            name: 'WWTP — Industrial Zone Input',
            sampleType: 'influent',
            notes: 'Elevated trade effluent from Western Sydney industrial precinct.',
            params: {
              ph: 6.2, cod: 1620, bod5: 810, tss: 520, oilgrease: 88, tn: 72, tp: 18,
              nh3n: 48, tds: 1180, conductivity: 1680, temperature: 30, turbidity: 340,
              alkalinity: 90, hardness: 285, chloride: 168, zinc: 0.72, chromium: 0.22,
              nickel: 0.18, copper: 0.28, lead: 0.12, arsenic: 0.018,
              cadmium: 0.008, mercury: 0.002, flow: 26000,
            },
          },
        ],
      },
      {
        name: 'Hunter Valley Abattoir & Meat Processing',
        address: '5 Meatworks Drive, Maitland, NSW 2320',
        postcode: '2320',
        country: 'AU',
        permitRef: `EPA-NSW-${year}-002`,
        permitIssued: `${year}-02-01`,
        permitExpiry: `${year + 3}-01-31`,
        dischargeRoute: 'Land Application (EPA Approved)',
        standard: 'AU_EPA',
        samples: [
          {
            name: 'Abattoir — Slaughter Day Operations',
            sampleType: 'influent',
            notes: 'Combined kill floor and hide-curing wastewater. High BOD and suspended solids.',
            params: {
              ph: 6.8, cod: 4800, bod5: 2880, tss: 1850, oilgrease: 620, tn: 185, tp: 38,
              nh3n: 125, tds: 2200, conductivity: 3400, temperature: 28, turbidity: 680,
              alkalinity: 320, hardness: 180, chloride: 1800, zinc: 0.28, chromium: 0.06,
              nickel: 0.08, copper: 0.14, lead: 0.02, arsenic: 0.004,
              cadmium: 0.002, mercury: 0.001, flow: 650,
            },
          },
          {
            name: 'Abattoir — Full Production (Max Load)',
            sampleType: 'influent',
            notes: 'Maximum throughput day. Rendering and blood collection included.',
            params: {
              ph: 6.5, cod: 9500, bod5: 5700, tss: 3800, oilgrease: 1450, tn: 380, tp: 85,
              nh3n: 260, tds: 4200, conductivity: 6500, temperature: 32, turbidity: 1250,
              alkalinity: 485, hardness: 210, chloride: 3500, zinc: 0.55, chromium: 0.12,
              nickel: 0.15, copper: 0.28, lead: 0.05, arsenic: 0.008,
              cadmium: 0.004, mercury: 0.002, flow: 920,
            },
          },
          {
            name: 'Abattoir — Yard Washdown / Clean Shift',
            sampleType: 'influent',
            notes: 'End-of-shift clean-down. Predominantly yard drainage and equipment wash.',
            params: {
              ph: 7.8, cod: 1200, bod5: 480, tss: 680, oilgrease: 185, tn: 68, tp: 12,
              nh3n: 42, tds: 880, conductivity: 1350, temperature: 24, turbidity: 285,
              alkalinity: 220, hardness: 165, chloride: 520, zinc: 0.18, chromium: 0.03,
              nickel: 0.05, copper: 0.08, lead: 0.015, arsenic: 0.003,
              cadmium: 0.001, mercury: 0.0005, flow: 380,
            },
          },
        ],
      },
      {
        name: 'Perth Coastal Desalination & Industrial',
        address: '180 Ocean Drive, Rockingham, WA 6168',
        postcode: '6168',
        country: 'AU',
        permitRef: `EPA-WA-${year}-003`,
        permitIssued: `${year}-04-01`,
        permitExpiry: `${year + 5}-03-31`,
        dischargeRoute: 'Cockburn Sound (Marine Discharge)',
        standard: 'AU_EPA',
        samples: [
          {
            name: 'Coastal — Saline Process Water',
            sampleType: 'influent',
            notes: 'Seawater-based cooling and process water. High TDS and conductivity.',
            params: {
              ph: 8.1, cod: 185, bod5: 42, tss: 185, oilgrease: 28, tn: 18, tp: 2.2,
              nh3n: 8, tds: 38000, conductivity: 52000, temperature: 20, turbidity: 45,
              alkalinity: 2800, hardness: 6400, chloride: 22000, zinc: 0.08, chromium: 0.01,
              nickel: 0.02, copper: 0.03, lead: 0.005, arsenic: 0.002,
              cadmium: 0.001, mercury: 0.0002, flow: 1200,
            },
          },
          {
            name: 'Coastal — Blended Industrial Discharge',
            sampleType: 'influent',
            notes: 'Mixed saline process water with mining chemicals. Brackish-saline.',
            params: {
              ph: 7.5, cod: 620, bod5: 185, tss: 380, oilgrease: 95, tn: 42, tp: 8.5,
              nh3n: 28, tds: 14500, conductivity: 21000, temperature: 24, turbidity: 180,
              alkalinity: 1200, hardness: 2800, chloride: 8800, zinc: 1.2, chromium: 0.45,
              nickel: 0.38, copper: 0.52, lead: 0.18, arsenic: 0.042,
              cadmium: 0.012, mercury: 0.003, flow: 850,
            },
          },
          {
            name: 'Coastal — Post-Rainfall Runoff',
            sampleType: 'influent',
            notes: 'Site surface runoff following heavy rainfall. Diluted but elevated suspended solids.',
            params: {
              ph: 7.9, cod: 245, bod5: 88, tss: 820, oilgrease: 38, tn: 22, tp: 4.5,
              nh3n: 12, tds: 5800, conductivity: 8500, temperature: 18, turbidity: 580,
              alkalinity: 480, hardness: 1100, chloride: 3200, zinc: 0.42, chromium: 0.08,
              nickel: 0.12, copper: 0.15, lead: 0.06, arsenic: 0.008,
              cadmium: 0.003, mercury: 0.0008, flow: 2200,
            },
          },
        ],
      },
    ],
  };
}

function buildEuData(userName: string): CountryData {
  const year = new Date().getFullYear();
  return {
    customerName: `${userName} Umwelttechnik`,
    company: `${userName} GmbH & Co. KG`,
    phone: '+49 89 1234 5678',
    email: `info@${userName.toLowerCase().replace(/\s+/g, '')}-water.de`,
    address: 'Industriestraße 45, 80339 München',
    country: 'DE',
    standard: 'EU_UWWTD',
    sites: [
      {
        name: 'München Klärwerk Süd',
        address: 'Klärwerksweg 12, 81379 München',
        postcode: '81379',
        country: 'DE',
        permitRef: `EU-UWWTD-${year}-DE-001`,
        permitIssued: `${year}-01-01`,
        permitExpiry: `${year + 4}-12-31`,
        dischargeRoute: 'Isar (Oberflächengewässer)',
        standard: 'EU_UWWTD',
        samples: [
          {
            name: 'Klärwerk — Normalbetrieb (Trockenwetter)',
            sampleType: 'influent',
            notes: 'Häusliches Abwasser plus Gewerbebetriebe. UWWTD 91/271/EWG Monitoring.',
            params: {
              ph: 7.4, cod: 420, bod5: 210, tss: 265, oilgrease: 20, tn: 48, tp: 7.5,
              nh3n: 32, tds: 620, conductivity: 920, temperature: 13, turbidity: 95,
              alkalinity: 245, hardness: 305, chloride: 112, zinc: 0.20, chromium: 0.04,
              nickel: 0.07, copper: 0.10, lead: 0.03, arsenic: 0.006, flow: 32000,
            },
          },
          {
            name: 'Klärwerk — Regenerationsereignis (Mischwasser)',
            sampleType: 'influent',
            notes: 'Starkregen — Mischkanalisation. Erhöhter Zufluss durch Fremdwasser.',
            params: {
              ph: 7.7, cod: 210, bod5: 98, tss: 520, oilgrease: 16, tn: 25, tp: 3.5,
              nh3n: 16, tds: 340, conductivity: 490, temperature: 11, turbidity: 310,
              alkalinity: 165, hardness: 215, chloride: 72, zinc: 0.42, chromium: 0.03,
              nickel: 0.05, copper: 0.07, lead: 0.09, arsenic: 0.004, flow: 72000,
            },
          },
          {
            name: 'Klärwerk — Industrieeinleitung (Störfall)',
            sampleType: 'influent',
            notes: 'Ungeplante Industrieeinleitung. Erhöhte CSB und Schwermetalle.',
            params: {
              ph: 5.5, cod: 2200, bod5: 1080, tss: 780, oilgrease: 110, tn: 95, tp: 24,
              nh3n: 68, tds: 1420, conductivity: 2050, temperature: 31, turbidity: 420,
              alkalinity: 78, hardness: 345, chloride: 210, zinc: 0.88, chromium: 0.28,
              nickel: 0.35, copper: 0.42, lead: 0.14, arsenic: 0.022, flow: 38000,
            },
          },
        ],
      },
      {
        name: 'Brauerei Müller — Sudhaus Abwasser',
        address: 'Brauereistraße 8, 83278 Traunstein',
        postcode: '83278',
        country: 'DE',
        permitRef: `EU-UWWTD-${year}-DE-002`,
        permitIssued: `${year}-03-15`,
        permitExpiry: `${year + 3}-03-14`,
        dischargeRoute: 'Kommunale Kläranlage (Indirekteinleitung)',
        standard: 'EU_UWWTD',
        samples: [
          {
            name: 'Brauerei — Normaler Sudtag',
            sampleType: 'influent',
            notes: 'Sudhaus-Abwasser. Hopfenbitterstoffe, Hefen, Malzrückstände.',
            params: {
              ph: 5.8, cod: 3600, bod5: 2160, tss: 980, oilgrease: 45, tn: 52, tp: 12,
              nh3n: 28, tds: 1850, conductivity: 2750, temperature: 30, turbidity: 420,
              alkalinity: 185, hardness: 165, chloride: 280, zinc: 0.08, chromium: 0.01,
              nickel: 0.02, copper: 0.06, lead: 0.005, arsenic: 0.002, flow: 480,
            },
          },
          {
            name: 'Brauerei — Hochsaison (Oktoberfest)',
            sampleType: 'influent',
            notes: 'Saisonale Überkapazität. Dreifacher Sudausstoß. Maximale Abwasserlast.',
            params: {
              ph: 5.4, cod: 8400, bod5: 5040, tss: 2200, oilgrease: 125, tn: 145, tp: 35,
              nh3n: 92, tds: 4200, conductivity: 6400, temperature: 35, turbidity: 860,
              alkalinity: 280, hardness: 185, chloride: 620, zinc: 0.18, chromium: 0.02,
              nickel: 0.04, copper: 0.12, lead: 0.008, arsenic: 0.003, flow: 920,
            },
          },
          {
            name: 'Brauerei — CIP-Reinigung (Tankwäsche)',
            sampleType: 'influent',
            notes: 'CIP-Ablauf. NaOH und Peressigsäure. Extremer pH, hohe Leitfähigkeit.',
            params: {
              ph: 12.5, cod: 2800, bod5: 680, tss: 185, oilgrease: 38, tn: 22, tp: 6,
              nh3n: 12, tds: 9800, conductivity: 14500, temperature: 62, turbidity: 95,
              alkalinity: 5600, hardness: 68, chloride: 3200, zinc: 0.04, chromium: 0.12,
              nickel: 0.08, copper: 0.10, lead: 0.025, arsenic: 0.004, flow: 185,
            },
          },
        ],
      },
      {
        name: 'Hamburg Chemiewerk Wilhelmsburg',
        address: 'Chemiepark 22, 21109 Hamburg',
        postcode: '21109',
        country: 'DE',
        permitRef: `EU-UWWTD-${year}-DE-003`,
        permitIssued: `${year}-05-01`,
        permitExpiry: `${year + 4}-04-30`,
        dischargeRoute: 'Elbe (Direkteinleitung)',
        standard: 'EU_UWWTD',
        samples: [
          {
            name: 'Chemiewerk — Standardbetrieb',
            sampleType: 'influent',
            notes: 'Organische Chemiesynthese. Lösungsmittelrückstände, Schwermetalle.',
            params: {
              ph: 4.8, cod: 5400, bod5: 1080, tss: 820, oilgrease: 185, tn: 68, tp: 15,
              nh3n: 38, tds: 6500, conductivity: 9200, temperature: 38, turbidity: 580,
              alkalinity: 52, hardness: 680, chloride: 2800, zinc: 4.2, chromium: 2.8,
              nickel: 1.8, copper: 1.2, lead: 0.62, arsenic: 0.12, flow: 280,
            },
          },
          {
            name: 'Chemiewerk — Produktionsspitze',
            sampleType: 'influent',
            notes: 'Maximale Produktionsauslastung. Reaktorwaschwasser inklusive.',
            params: {
              ph: 3.8, cod: 12000, bod5: 2400, tss: 1850, oilgrease: 480, tn: 145, tp: 35,
              nh3n: 85, tds: 14500, conductivity: 21000, temperature: 45, turbidity: 1200,
              alkalinity: 28, hardness: 1200, chloride: 6500, zinc: 9.8, chromium: 6.5,
              nickel: 4.2, copper: 3.1, lead: 1.8, arsenic: 0.38, flow: 420,
            },
          },
          {
            name: 'Chemiewerk — Havariage (Chemikalienaustritt)',
            sampleType: 'influent',
            notes: 'Störfallableitung nach Tankleck. Notfallbehandlung erforderlich.',
            params: {
              ph: 2.2, cod: 28000, bod5: 3200, tss: 4200, oilgrease: 1200, tn: 280, tp: 65,
              nh3n: 185, tds: 48000, conductivity: 68000, temperature: 22, turbidity: 3800,
              alkalinity: 0, hardness: 2400, chloride: 28000, zinc: 28, chromium: 18,
              nickel: 12, copper: 8.5, lead: 4.8, arsenic: 1.2, flow: 85,
            },
          },
        ],
      },
    ],
  };
}

function buildUsData(userName: string): CountryData {
  const year = new Date().getFullYear();
  return {
    customerName: `${userName} Environmental LLC`,
    company: `${userName} Water Technologies, Inc.`,
    phone: '+1 312 555 0190',
    email: `contact@${userName.toLowerCase().replace(/\s+/g, '')}-water.com`,
    address: '450 Industrial Parkway, Chicago, IL 60601',
    country: 'US',
    standard: 'US_EPA',
    sites: [
      {
        name: 'Chicago South WWTP',
        address: '3500 Treatment Way, Chicago, IL 60609',
        postcode: '60609',
        country: 'US',
        permitRef: `NPDES-IL-${year}-001`,
        permitIssued: `${year}-01-01`,
        permitExpiry: `${year + 5}-12-31`,
        dischargeRoute: 'Calumet River (NPDES Permitted)',
        standard: 'US_EPA',
        samples: [
          {
            name: 'WWTP — Normal Dry Weather',
            sampleType: 'influent',
            notes: 'Domestic wastewater plus light commercial. NPDES permit monitoring per 40 CFR Part 122.',
            params: {
              ph: 7.1, cod: 395, bod5: 200, tss: 255, oilgrease: 19, tn: 44, tp: 7.0,
              nh3n: 29, tds: 545, conductivity: 800, temperature: 16, turbidity: 80,
              alkalinity: 200, hardness: 265, chloride: 98, zinc: 0.19, chromium: 0.04,
              nickel: 0.06, copper: 0.09, lead: 0.03, arsenic: 0.006, flow: 28000,
            },
          },
          {
            name: 'WWTP — Spring Snowmelt (High Flow)',
            sampleType: 'influent',
            notes: 'Seasonal snowmelt event. Combined sewer system. Diluted influent, high flow.',
            params: {
              ph: 7.5, cod: 185, bod5: 82, tss: 485, oilgrease: 11, tn: 20, tp: 2.9,
              nh3n: 13, tds: 285, conductivity: 412, temperature: 8, turbidity: 280,
              alkalinity: 135, hardness: 185, chloride: 62, zinc: 0.35, chromium: 0.02,
              nickel: 0.04, copper: 0.05, lead: 0.07, arsenic: 0.004, flow: 68000,
            },
          },
          {
            name: 'WWTP — Industrial Pretreatment Bypass',
            sampleType: 'influent',
            notes: 'Industrial user pretreatment failure. Elevated metals and COD.',
            params: {
              ph: 5.9, cod: 1900, bod5: 850, tss: 620, oilgrease: 98, tn: 82, tp: 20,
              nh3n: 55, tds: 1350, conductivity: 1920, temperature: 29, turbidity: 360,
              alkalinity: 88, hardness: 310, chloride: 195, zinc: 0.82, chromium: 0.32,
              nickel: 0.25, copper: 0.38, lead: 0.15, arsenic: 0.022, flow: 34000,
            },
          },
        ],
      },
      {
        name: 'Texas Gulf Coast Petrochemical Facility',
        address: '12 Refinery Row, Pasadena, TX 77501',
        postcode: '77501',
        country: 'US',
        permitRef: `NPDES-TX-${year}-002`,
        permitIssued: `${year}-02-01`,
        permitExpiry: `${year + 4}-01-31`,
        dischargeRoute: 'Houston Ship Channel (TPDES Permitted)',
        standard: 'US_EPA',
        samples: [
          {
            name: 'Petrochemical — Normal Refinery Ops',
            sampleType: 'influent',
            notes: 'Crude oil refinery process water. Phenolics, BTEX, heavy oil-water emulsions.',
            params: {
              ph: 6.8, cod: 3800, bod5: 760, tss: 680, oilgrease: 285, tn: 58, tp: 8.5,
              nh3n: 38, tds: 4500, conductivity: 6800, temperature: 40, turbidity: 380,
              alkalinity: 165, hardness: 480, chloride: 1850, zinc: 0.85, chromium: 0.28,
              nickel: 0.42, copper: 0.35, lead: 0.18, arsenic: 0.055,
              phenol: 18, sulfide: 12, flow: 1850,
            },
          },
          {
            name: 'Petrochemical — Turnaround / Maintenance',
            sampleType: 'influent',
            notes: 'Scheduled turnaround. Tank cleanouts, equipment flush. Peak contamination load.',
            params: {
              ph: 4.5, cod: 9500, bod5: 1900, tss: 2200, oilgrease: 1850, tn: 125, tp: 22,
              nh3n: 88, tds: 12000, conductivity: 18500, temperature: 48, turbidity: 1650,
              alkalinity: 88, hardness: 1200, chloride: 5800, zinc: 3.2, chromium: 1.8,
              nickel: 2.4, copper: 1.6, lead: 0.85, arsenic: 0.22,
              phenol: 85, sulfide: 48, flow: 2800,
            },
          },
          {
            name: 'Petrochemical — Stormwater Runoff',
            sampleType: 'influent',
            notes: 'Site stormwater during hurricane season. Contaminated runoff from storage areas.',
            params: {
              ph: 7.2, cod: 1250, bod5: 380, tss: 1850, oilgrease: 185, tn: 45, tp: 9.5,
              nh3n: 28, tds: 2800, conductivity: 4200, temperature: 28, turbidity: 1200,
              alkalinity: 280, hardness: 620, chloride: 1200, zinc: 1.5, chromium: 0.65,
              nickel: 0.88, copper: 0.72, lead: 0.38, arsenic: 0.085,
              phenol: 12, sulfide: 8, flow: 4500,
            },
          },
        ],
      },
      {
        name: 'California Silicon Valley Semiconductor Fab',
        address: '1 Chip Boulevard, San Jose, CA 95134',
        postcode: '95134',
        country: 'US',
        permitRef: `NPDES-CA-${year}-003`,
        permitIssued: `${year}-06-01`,
        permitExpiry: `${year + 5}-05-31`,
        dischargeRoute: 'San Francisco Bay (POTW via SCVWD)',
        standard: 'US_EPA',
        samples: [
          {
            name: 'Semiconductor Fab — Normal Process Water',
            sampleType: 'influent',
            notes: 'DI water reject, chemical mechanical planarization (CMP) slurry. Low BOD, high metals.',
            params: {
              ph: 4.2, cod: 850, bod5: 42, tss: 380, oilgrease: 8, tn: 18, tp: 4.5,
              nh3n: 8, tds: 2800, conductivity: 4200, temperature: 22, turbidity: 185,
              alkalinity: 38, hardness: 285, chloride: 620, zinc: 12, chromium: 0.95,
              nickel: 3.8, copper: 8.5, lead: 0.28, arsenic: 0.15,
              cadmium: 0.08, mercury: 0.02, flow: 450,
            },
          },
          {
            name: 'Semiconductor Fab — Acid Bath Changeover',
            sampleType: 'influent',
            notes: 'HF and H2SO4 bath replacement. Extreme pH depression, very high metal content.',
            params: {
              ph: 1.5, cod: 2200, bod5: 85, tss: 920, oilgrease: 15, tn: 42, tp: 12,
              nh3n: 18, tds: 8500, conductivity: 12800, temperature: 28, turbidity: 580,
              alkalinity: 0, hardness: 850, chloride: 2500, zinc: 45, chromium: 4.5,
              nickel: 18, copper: 38, lead: 1.8, arsenic: 0.85,
              cadmium: 0.45, mercury: 0.12, flow: 280,
            },
          },
          {
            name: 'Semiconductor Fab — Coolant Blowdown',
            sampleType: 'influent',
            notes: 'Chiller tower blowdown. Corrosion inhibitors, biocides, scale inhibitors.',
            params: {
              ph: 8.5, cod: 620, bod5: 88, tss: 285, oilgrease: 18, tn: 28, tp: 6.5,
              nh3n: 12, tds: 3800, conductivity: 5600, temperature: 30, turbidity: 125,
              alkalinity: 680, hardness: 1200, chloride: 980, zinc: 2.8, chromium: 0.42,
              nickel: 0.85, copper: 1.2, lead: 0.08, arsenic: 0.025,
              cadmium: 0.02, mercury: 0.005, flow: 185,
            },
          },
        ],
      },
    ],
  };
}

// ─── Country Router ───────────────────────────────────────────────────────────

function getCountryData(country: string, userName: string): CountryData {
  switch (country) {
    case 'AU': case 'NZ': return buildAuData(userName);
    case 'DE': case 'FR': case 'NL': case 'BE': case 'AT': case 'CH': return buildEuData(userName);
    case 'US': case 'CA': return buildUsData(userName);
    default: return buildGbData(userName);
  }
}

// ─── Remote (PHP API) ─────────────────────────────────────────────────────────

export async function seedUserDataRemote(
  targetUserId: number,
  country: string,
  userName: string
): Promise<{ success: boolean; message: string }> {
  const base = getApiBase();
  const res = await fetch(`${base}/admin/seed-user.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ userId: targetUserId, country, userName }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as { success: boolean; message: string };
}

// ─── Local (localStorage) Fallback ───────────────────────────────────────────

export function seedUserDataLocally(
  userId: string | number,
  country: string,
  userName: string
): void {
  const uid = String(userId);
  const data = getCountryData(country, userName);

  // ── Create Customer ──
  const customerId = `seeded_${uid}_${Date.now()}`;
  const customers: any[] = JSON.parse(localStorage.getItem('rb_customers') || '[]');
  const newCustomer = {
    id: customerId,
    userId: uid,
    name: data.customerName,
    company: data.company,
    phone: data.phone,
    email: data.email,
    address: data.address,
    country: data.country,
    standard: data.standard,
    createdAt: new Date().toISOString(),
  };
  customers.push(newCustomer);
  localStorage.setItem('rb_customers', JSON.stringify(customers));

  // ── Create Sites + Samples ──
  data.sites.forEach((site, siteIdx) => {
    const savedSite = saveLocalSite({
      customerId,
      name: site.name,
      address: site.address,
      postcode: site.postcode,
      country: site.country,
      permitRef: site.permitRef,
      dischargeRoute: site.dischargeRoute,
      standard: site.standard,
      notes: `Permit issued: ${site.permitIssued} — expires: ${site.permitExpiry}`,
    });
    const siteId = savedSite.id;

    site.samples.forEach((sample, sampleIdx) => {
      saveLocalInfluentSample({
        id: Date.now() + siteIdx * 100 + sampleIdx,
        userId: Number(uid) || 0,
        customerId: Number(customerId) || null,
        siteId,
        sampleName: sample.name,
        reportNumber: `RB-SEED-${new Date().getFullYear()}-${String(siteIdx + 1).padStart(2, '0')}${String(sampleIdx + 1).padStart(2, '0')}`,
        sampleType: sample.sampleType as any,
        params: sample.params,
        limits: null,
        standard: site.standard,
        notes: sample.notes,
        linkedSampleId: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    });
  });
}
